import React, { useRef, useState, useEffect } from "react";
import { fetchSubscriptionPlans } from "./api/subscription";
import styled from "styled-components";
import logo from "./WiseShift.png"; 
import SignupForm from './SignUpForm';
import ContactUs from './ContactUs';
import { FaBars, FaTimes, FaInstagram, FaYoutube, FaCopyright } from "react-icons/fa"; 

// ========== Styled Components ==========

const Wrapper = styled.div`
  font-family: 'Segoe UI', sans-serif;
  background: #FCFCF1; 
  color: #111;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 32px;
  background: #FCFCF1;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);
`;

const NavItem = styled.button`
  background: none;
  border: none;
  font-size: 1.4em;
  font-weight: 700;
  color: #b30000;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 6px;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #fce5e5;
  }
`;

const NavContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Section = styled.section`
  margin: 10px auto 0 auto;
  width: 100%;
  min-height: unset;
  padding: 0 20px;
  box-sizing: border-box;
`;

const DiscoverSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100vw; 
  overflow: hidden;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; 
  padding: 0;
  text-align: center;
`;

const DiscoverTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 900;
  margin: 0 0 20px 0;
  color: #fff;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1.9rem; /* Mobile-friendly */
    max-width: 90%;
    line-height: 1.2;
  }
`;

const DiscoverImageBlock = styled.div`
  position: absolute;
  inset: 0; 
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${({ src }) =>
    `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${src})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
`;



const DiscoverContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center; /* vertically center content */
  align-items: center;
  padding: 40px 20px;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }
`;

const SliderWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  margin: 20px auto 10px auto;
  padding: 24px 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - 40px);  
  max-width: 1400px;
  height: 100%;
  
  @media (max-width: 768px) {
    width: calc(100% - 20px);  
    padding: 20px 16px;
    margin: 10px auto 20px auto;
  }
`;

const SliderDisplay = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 20px;

  @media (max-width: 768px) {
    flex-direction: column; /* Stack vertically on mobile */
    gap: 20px;
    padding: 15px;
  }
`;

const FeatureImage = styled.img`
  width: 60%;          /* Take majority of space on desktop */
  max-width: 600px;    /* Prevent excessive scaling */
  height: auto;
  object-fit: cover;
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 1024px) {
    width: 70%;
    max-width: 500px;
  }

  @media (max-width: 768px) {
    width: 85%;        /* Nearly full width on tablets & mobile */
    max-width: 380px;
  }

  @media (max-width: 480px) {
    width: 95%;        /* Small phones = full width */
    max-width: 320px;
  }
`;



const SliderNav = styled.div`
  display: flex;
  margin-top: 18px;
  justify-content: center;
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  color: #b30000;
  font-size: 2.2em;  // bigger if needed
  padding: 0 12px;
  border-radius: 0;
  box-shadow: none;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #660000;
  }
`;

const FeatureArrowsMobile = styled.div`
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const FeatureArrowsDesktop = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Dot = styled.button`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#b30000" : "#ccc")};
  border: none;
  margin: 0 5px;
  outline: none;
  cursor: pointer;
`;

const FlexRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 44px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const SubCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 14px rgba(0,0,0,0.07);
  flex: 1 1 270px;
  margin: 12px;
  padding: 18px 18px 26px;
  text-align: center;
  min-width: 230px;
  max-width: 310px;
`;

const SubTitle = styled.div`
  font-size: 1.18em;
  font-weight: 600;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
`;

const BillingToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 9px;
  margin: 16px 0 16px 0;
`;

const ToggleButton = styled.button`
  background: ${({ active }) => (active ? "#b30000" : "#eee")};
  color: ${({ active }) => (active ? "#fff" : "#333")};
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 18px;
  margin: 0 3px;
  font-size: 1em;
  cursor: pointer;
`;

const CTA = styled.button`
  display: block;
  width: 100%;
  font-size: 1.1em;
  margin: 18px auto 4px;
  background: #b30000;
  color: #fff;
  border: none;
  padding: 11px 0;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 0 4px #ffc0cb;
  &:hover {
    background: #990000;
  }
`;

const Logo = styled.img`
  height: 90px;
  border-radius: 8px;
  @media (max-width: 768px) {
    height: 70px;
    border-radius: 6px;
    margin-right: auto;
    margin-left: auto;
  }
`;  

const Footer = styled.footer`
  background: #1a1820;
  color: #fff;
  padding: 20px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 1.13em;
  letter-spacing: 0.01em;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const FooterCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 1.2rem;
  color: white;
`;

const FooterIcons = styled.div`
  position: absolute;
  right: 32px;
  display: flex;
  gap: 18px;

  & > a {
    color: #b30000;
    font-size: 1.8rem;
    transition: color 0.2s ease;
  }
  & > a:hover {
    color: #ff6666;
  }

  @media (max-width: 768px) {
    position: static;
    gap: 20px;
    justify-content: center;
    width: 100%;
  }
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: ${({ open }) => (open ? "0" : "-240px")};
  width: 240px;
  height: 100vh;
  background: #fff;
  box-shadow: 2px 0 10px rgba(18,18,18,0.07);
  z-index: 120;
  display: flex;
  flex-direction: column;
  transition: left 0.25s;
  padding: 24px 0 0 0;

  @media (min-width: 769px) {
    display: none;
  }
`;

const SidebarNavItem = styled.button`
  background: none;
  border: none;
  font-size: 1.18em;
  color: #b30000;
  margin: 20px 0 0 24px;
  text-align: left;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover {
    background: #fce5e5;
  }
`;

const Overlay = styled.div`
  display: ${({ open }) => (open ? "block" : "none")};
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.18);
  z-index: 110;
  @media (min-width: 769px) {
    display: none;
  }
`;
const MenuButton = styled.button`
  background: none;
  border: none;
  color: #b30000;
  font-size: 1.9em;
  cursor: pointer;
  display: block;
  z-index: 130;
  @media (min-width: 769px) {
    display: none;
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginButton = styled.button`
  background: none;
  border: 2px solid #b30000;
  color: #b30000;
  font-size: 1em;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #b30000;
    color: #fff;
  }
`;

const RegisterButton = styled.button`
  background: #b30000;
  border: 2px solid #b30000;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #990000;
    border-color: #990000;
  }
`;

// ========== Constants (Replace with your own data) ==========
const sliderImages = [
  { src: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=600&q=80", alt: "Dashboard" },
  { src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=600&q=80", alt: "Analytics" },
  { src: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80", alt: "Graph" },
  { src: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=600&q=80", alt: "Workflow" },
];

const features = [
  {
    title: "AI-Powered Scheduling",
    description: "Automatically creates optimized schedules based on store hours, staffing needs, and employee availability.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Employee Availability Input",
    description: "Employees submit their available days and times, and WiseShift assigns suitable shifts.",
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Instant SMS Schedule Delivery",
    description: "Employees instantly receive schedules via text message — no login or app required.",
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Multi-Week Scheduling Options",
    description: "Generate weekly, bi-weekly, 3-week, or 4-week schedules with one click.",
    img: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Fair Shift Assignment",
    description: "Ensures each employee only receives the number of hours assigned to them.",
    img: "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Overtime Prevention",
    description: "Alerts managers when shifts exceed allowable hours or create overtime issues.",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Break & Shift Rules",
    description: "Automatically adds 30-minute breaks and enforces maximum shift lengths.",
    img: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Multi-Store Dashboard",
    description: "Manage multiple store locations under one manager account.",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Auto-Renewing Subscription",
    description: "Stores receive SMS reminders before renewal; subscriptions auto-renew unless canceled.",
    img: "https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "One-Time Setup",
    description: "Enter store and employee information once — WiseShift handles the rest for future schedules.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
  }
];

// ...existing code...

// ========== Components ==========

function App() {
  const featureRef = useRef();
  const subscriptionRef = useRef();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const scrollTo = (ref) => {
    setSidebarOpen(false);
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [showContactUs, setShowContactUs] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanSpan, setSelectedPlanSpan] = useState(null);

  // Image slider state
  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setImgIdx(prev => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Feature slider state
  const [featIdx, setFeatIdx] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setFeatIdx(prev => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Subscription plans state
  const [plans, setPlans] = useState([]);
  // Billing period state (per plan id)
  const [billing, setBilling] = useState({});

  useEffect(() => {
    fetchSubscriptionPlans().then(data => {
      setPlans(data);
      // Set default billing for each plan
      const billingInit = {};
      data.forEach(plan => {
        billingInit[plan.id] = "monthly";
      });
      setBilling(billingInit);
    });
  }, []);

  return (
    <Wrapper>
      {/* Header */}
      <Header>
        <MenuButton onClick={() => setSidebarOpen(true)} aria-label="Open Menu">
          <FaBars />
        </MenuButton>
        <Logo src={logo} alt="Logo" style={{ height: 90, borderRadius: 8 }} />
        <NavContainer>
          <NavItem onClick={() => featureRef.current.scrollIntoView({ behavior: "smooth" })}>Features</NavItem>
          <NavItem onClick={() => subscriptionRef.current.scrollIntoView({ behavior: "smooth" })}>Plans</NavItem>
          <NavItem
            as="a"
            href="#"
            style={{ textDecoration: "none" }}
            onClick={e => { e.preventDefault(); setShowContactUs(true); }}
          >
            Contact Us
          </NavItem>
        </NavContainer>
        <HeaderButtons>
          <LoginButton onClick={() => window.location.href = 'https://web.wiseshift.ca/login'}>
            Login
          </LoginButton>
          <RegisterButton onClick={() => subscriptionRef.current.scrollIntoView({ behavior: "smooth" })}>
            Register
          </RegisterButton>
        </HeaderButtons>
      </Header>
      {showContactUs && (
        <ContactUs onClose={() => setShowContactUs(false)} />
      )}
      <Sidebar open={sidebarOpen}>
        <SidebarNavItem style={{ alignSelf: "flex-end", margin: "0 16px 14px 0" }} onClick={() => setSidebarOpen(false)}>
          <FaTimes size={24} />
        </SidebarNavItem>
        <SidebarNavItem onClick={() => scrollTo(featureRef)}>Features</SidebarNavItem>
        <SidebarNavItem onClick={() => scrollTo(subscriptionRef)}>Plans</SidebarNavItem>
        <SidebarNavItem onClick={() => {
          setSidebarOpen(false);
          setShowContactUs(true);
        }}>
          Contact Us
        </SidebarNavItem>
        <SidebarNavItem onClick={() => {
          setSidebarOpen(false);
          window.location.href = 'https://web.wiseshift.ca/login';
        }}>
          Login
        </SidebarNavItem>
        <SidebarNavItem onClick={() => scrollTo(subscriptionRef)}>Register</SidebarNavItem>
      </Sidebar>
      <Overlay open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      {/* Main Image Slider */}
      <DiscoverSection>
        <DiscoverContent>
          <DiscoverTitle>Discover What We Offer</DiscoverTitle>
          <DiscoverImageBlock>
            <BackgroundImage src={sliderImages[imgIdx].src} />
          </DiscoverImageBlock>
          <SliderNav>
            {sliderImages.map((img, i) => (
              <Dot key={i} active={i === imgIdx} onClick={() => setImgIdx(i)} />
            ))}
          </SliderNav>
        </DiscoverContent>
      </DiscoverSection>

      {/* Features Slider */}
      <Section ref={featureRef} style={{ scrollMarginTop: "125px" }}>
        <SliderWrapper>
          <h2>Key Features</h2>
          
          {/* Mobile Up Arrow */}
          <FeatureArrowsMobile>
            <ArrowButton onClick={() => setFeatIdx((featIdx - 1 + features.length) % features.length)}>
              ▲
            </ArrowButton>
          </FeatureArrowsMobile>

          <SliderDisplay>
            {/* Desktop Left Arrow */}
            <FeatureArrowsDesktop>
              <ArrowButton onClick={() => setFeatIdx((featIdx - 1 + features.length) % features.length)}>
                ◀
              </ArrowButton>
            </FeatureArrowsDesktop>

            {/* Feature Text and Image */}
            <div style={{ flex: 1 }}>
              <h3>{features[featIdx].title}</h3>
              <p>{features[featIdx].description}</p>
            </div>
            <FeatureImage src={features[featIdx].img} alt={features[featIdx].title} />

            {/* Desktop Right Arrow */}
            <FeatureArrowsDesktop>
              <ArrowButton onClick={() => setFeatIdx((featIdx + 1) % features.length)}>
                ▶
              </ArrowButton>
            </FeatureArrowsDesktop>
          </SliderDisplay>

          {/* Mobile Down Arrow */}
          <FeatureArrowsMobile>
            <ArrowButton onClick={() => setFeatIdx((featIdx + 1) % features.length)}>
              ▼
            </ArrowButton>
          </FeatureArrowsMobile>
        </SliderWrapper>
      </Section>

      {/* Subscription Section */}
      <Section ref={subscriptionRef} style={{ scrollMarginTop: "125px" }}>
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>Subcription Plans</h2>
        <FlexRow>
          {plans.map(plan => (
            <SubCard key={plan.id}>
              <SubTitle>{plan.name}</SubTitle>
              <BillingToggle>
                <ToggleButton
                  active={billing[plan.id] === "monthly"}
                  onClick={() => setBilling(b => ({ ...b, [plan.id]: "monthly" }))}
                >
                  Monthly
                </ToggleButton>
                <ToggleButton
                  active={billing[plan.id] === "yearly"}
                  onClick={() => setBilling(b => ({ ...b, [plan.id]: "yearly" }))}
                >
                  Yearly
                </ToggleButton>
              </BillingToggle>
              <div style={{
                fontSize: "1.8em",
                fontWeight: 700,
                marginBottom: 12,
                color: "#1971c2"
              }}>
                ${billing[plan.id] === "monthly" ? plan.monthly_price : plan.yearly_price}
                <span style={{ fontSize: "0.6em", marginLeft: 4, color: "#333", fontWeight: 400 }}>
                  {billing[plan.id] === "monthly" ? "/mo" : "/yr"}
                </span>
              </div>
              <ul style={{
                textAlign: "left",
                margin: "0 auto 13px auto",
                maxWidth: 195,
                color: "#474747"
              }}>
                {Array.isArray(plan.features.details) && plan.features.details.map((f, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{f}</li>
                ))}
              </ul>
              <CTA 
                onClick={() => {
                  setSelectedPlan(plan.name);
                  setSelectedPlanSpan(billing[plan.id]);
                  setShowSignup(true);
                  console.log("Selected Plan:", plan.name, billing[plan.id]);
                }}>
                Get Started
              </CTA>
            </SubCard>
          ))}
        </FlexRow>
        <div style={{
          textAlign: "center",
          marginTop: "40px",
          fontSize: "1.4rem",
          fontWeight: "bold",
          color: "#b30000",
          backgroundColor: "#fff0f0",
          padding: "20px 15px",
          borderRadius: "10px",
          maxWidth: "600px",
          marginLeft: "auto",
          marginRight: "auto",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)"
        }}>
          TRY 1 MONTH FREE TRIAL NOW
        </div>
        <br />
      </Section>

      {/* Footer */}
      <Footer>
        <FooterCenter>
          <FaCopyright />
          2025
        </FooterCenter>
        <FooterIcons>
          <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
            <FaYoutube />
          </a>
          <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FaInstagram />
          </a>
        </FooterIcons>
      </Footer>
      {showSignup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px", 
            boxSizing: "border-box"
          }}
        >
          <SignupForm
            onClose={() => setShowSignup(false)}
            selectedPlan={selectedPlan}
            selectedPlanSpan={selectedPlanSpan}
          /> 
        </div>
      )}
    </Wrapper>
  );
}

export default App;
